import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import {
  X,
  GitBranch,
  Sparkles,
  Info,
  Loader2,
  CheckCircle2,
} from 'lucide-react';
import type { Book, CreateEditionInput, EditionType } from '../../../shared/types/index.js';
import { api } from '../../../shared/api/api.js';
import { useAuthStore } from '../../auth/store/useAuthStore.js';
import { canCreateNewEdition } from '../../books/components/book-list/bookListUtils.js';
import { EditionParentCallout, EditionTypeGrid } from './create-edition/index.js';
import { useBodyScrollLock } from '../../../shared/utils/scrollLock.js';

interface CreateEditionModalProps {
  parentBook: Book | null;
  isOpen: boolean;
  onClose: () => void;
  onEditionCreated: (newBook: Book) => void;
}

export const CreateEditionModal: React.FC<CreateEditionModalProps> = ({
  parentBook,
  isOpen,
  onClose,
  onEditionCreated,
}) => {
  const { user } = useAuthStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form states initialized from parentBook
  const [editionType, setEditionType] = useState<EditionType>('revisi');
  const [edition, setEdition] = useState('');
  const [title, setTitle] = useState('');
  const [originalTitle, setOriginalTitle] = useState('');
  const [publicationYear, setPublicationYear] = useState(new Date().getFullYear());
  const [pages, setPages] = useState<number>(0);
  const [language, setLanguage] = useState('Indonesia');
  const [editionNotes, setEditionNotes] = useState('');
  const [synopsis, setSynopsis] = useState('');
  const [coverUrl, setCoverUrl] = useState('');
  const [status, setStatus] = useState<'diajukan' | 'draft' | 'disetujui'>('diajukan');

  // Sync defaults when parentBook changes
  React.useEffect(() => {
    if (parentBook) {
      setTitle(parentBook.title);
      setOriginalTitle(parentBook.originalTitle || '');
      setPages(parentBook.pages || 0);
      setLanguage(parentBook.language || 'Indonesia');
      setSynopsis(parentBook.synopsis || '');
      setCoverUrl(parentBook.coverUrl || '');
      setEdition('Edisi 2 (Revisi)');
      setEditionNotes('');
      setError(null);
    }
  }, [parentBook]);

  useBodyScrollLock(isOpen);

  if (!isOpen || !parentBook) return null;

  const canPublish = canCreateNewEdition(parentBook, user);

  const handleTypeChange = (newType: EditionType) => {
    setEditionType(newType);
    if (newType === 'digital') {
      setEdition('Edisi Digital (E-Book)');
    } else if (newType === 'terjemahan') {
      setEdition('Edisi Terjemahan Inggris');
      setLanguage('English');
    } else if (newType === 'edisi_khusus') {
      setEdition('Edisi Khusus Kolektor');
    } else if (newType === 'cetakan_baru') {
      setEdition('Cetakan ke-2');
    } else if (newType === 'edisi_saku') {
      setEdition('Edisi Saku');
    } else {
      setEdition('Edisi 2 (Revisi)');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canPublish) {
      setError('Akses Ditolak: Penerbit tidak dapat menerbitkan edisi baru untuk naskah yang diajukan oleh Penulis.');
      return;
    }
    if (!edition.trim()) {
      setError('Label edisi tidak boleh kosong (contoh: "Edisi 2")');
      return;
    }
    if (!editionNotes.trim() || editionNotes.trim().length < 5) {
      setError('Catatan perubahan edisi wajib diisi (minimal 5 karakter)');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const payload: CreateEditionInput = {
        edition: edition.trim(),
        editionType,
        editionNotes: editionNotes.trim(),
        title: title.trim() || parentBook.title,
        originalTitle: originalTitle.trim() || undefined,
        publicationYear: Number(publicationYear),
        pages: Number(pages),
        language: language.trim(),
        synopsis: synopsis.trim() || undefined,
        coverUrl: coverUrl.trim() || undefined,
        status: user?.role === 'admin' ? status : 'diajukan',
      };

      const res = await api.createEdition(parentBook.id, payload);
      onEditionCreated(res.book);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Gagal menerbitkan edisi baru');
    } finally {
      setIsSubmitting(false);
    }
  };

  const modalContent = (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/70 backdrop-blur-xs p-4 overflow-y-auto animate-in fade-in duration-200"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[92vh] overflow-hidden shadow-2xl border border-slate-100 flex flex-col my-auto">
        {/* Header */}
        <div className="p-4 sm:p-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/80">
          <div className="flex items-center gap-2.5">
            <span className="p-2 bg-emerald-100 text-emerald-700 rounded-xl">
              <GitBranch className="w-5 h-5" />
            </span>
            <div>
              <h3 className="text-base font-bold text-slate-900">
                Terbitkan Edisi Baru (Hubungan Antar-Edisi)
              </h3>
              <p className="text-xs text-slate-500">
                Daftarkan edisi revisi, cetakan ulang, format digital, atau terjemahan resmi
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 p-1 rounded-lg hover:bg-slate-200 transition cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Parent Book Context Callout */}
        <EditionParentCallout parentBook={parentBook} />

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5 space-y-4">
          {error && (
            <div className="p-3 bg-rose-50 border border-rose-200 text-rose-700 text-xs rounded-xl flex items-center gap-2">
              <Info className="w-4 h-4 shrink-0 text-rose-500" />
              <span>{error}</span>
            </div>
          )}

          {/* Edition Type Selector Grid */}
          <EditionTypeGrid
            selectedType={editionType}
            onSelectType={handleTypeChange}
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Edition Label */}
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                Penamaan Edisi <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                value={edition}
                onChange={(e) => setEdition(e.target.value)}
                placeholder="Contoh: Edisi 2, Cetakan ke-3, Edisi Digital"
                required
                className="w-full text-xs px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white"
              />
            </div>

            {/* Publication Year */}
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                Tahun Terbit Edisi Baru <span className="text-rose-500">*</span>
              </label>
              <input
                type="number"
                min={1900}
                max={2100}
                value={publicationYear}
                onChange={(e) => setPublicationYear(Number(e.target.value))}
                required
                className="w-full text-xs px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white"
              />
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">
              Judul Buku Edisi Ini <span className="text-rose-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Biasanya sama dengan judul naskah induk"
              required
              className="w-full text-xs px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white"
            />
            <span className="text-[11px] text-slate-400 mt-0.5 block">
              Dapat disesuaikan jika merupakan terjemahan bahasa asing atau edisi khusus.
            </span>
          </div>

          {/* Change notes / Changelog */}
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">
              Catatan Perubahan Edisi (Changelog / Release Notes) <span className="text-rose-500">*</span>
            </label>
            <textarea
              rows={2}
              value={editionNotes}
              onChange={(e) => setEditionNotes(e.target.value)}
              placeholder="Jelaskan ringkasan perubahan pada edisi ini (contoh: Penambahan 3 bab baru mengenai Cloud Native, koreksi kesalahan rumus bab 4, dan perbaruan data statistik)."
              required
              className="w-full text-xs px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Pages */}
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                Jumlah Halaman
              </label>
              <input
                type="number"
                min={0}
                value={pages}
                onChange={(e) => setPages(Number(e.target.value))}
                className="w-full text-xs px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white"
              />
            </div>

            {/* Language */}
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                Bahasa Naskah
              </label>
              <input
                type="text"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                placeholder="Indonesia, English, dll"
                className="w-full text-xs px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white"
              />
            </div>
          </div>

          {/* Synopsis */}
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">
              Sinopsis / Deskripsi Edisi Ini
            </label>
            <textarea
              rows={2}
              value={synopsis}
              onChange={(e) => setSynopsis(e.target.value)}
              placeholder="Sinopsis singkat yang menggambarkan fokus atau kebaruan edisi ini"
              className="w-full text-xs px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white"
            />
          </div>

          {/* New UIBN Notice */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex items-start gap-2.5 text-xs text-emerald-800">
            <Sparkles className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold">Kode UIBN Baru Akan Diterbitkan Otomatis</p>
              <p className="text-[11px] text-emerald-700 mt-0.5">
                Sesuai standar UIBN, setiap edisi baru mendapatkan nomor identifikasi unik sendiri
                namun tetap terhubung secara hierarkis dalam silsilah buku induk.
              </p>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-xs font-semibold text-slate-600 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 rounded-lg transition cursor-pointer"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition flex items-center gap-1.5 shadow-xs disabled:opacity-50 cursor-pointer"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Menerbitkan Edisi...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Terbitkan Edisi & Hubungkan Silsilah
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};
